import { useEffect, useMemo, useRef, useState } from "react";

import { fetchRegionalMeasurements } from "../../utils/backend/pdxBackendClient";
import { getResultModeConfigForVariant } from "../../utils/results/resultModeConfig";
import { mapMeasurementDocToRow } from "./mapMeasurementRow";
import { mapSpirometryDocToRow } from "./mapSpirometryDocToRow";

function measurementRecordId(record) {
  return record?.measurementId || record?.recordingId || record?.docId || "";
}

function measurementDoc(record) {
  const id = measurementRecordId(record);

  return {
    id,
    data: () => record || {},
  };
}

function mapBackendMeasurementToRow(record, languageFallback) {
  const variant = record?.variant || "";
  const config = getResultModeConfigForVariant(variant);
  const doc = measurementDoc(record);
  const mapped =
    variant === "RecordingSpirometry"
      ? mapSpirometryDocToRow(doc, languageFallback)
      : mapMeasurementDocToRow(
          doc,
          config?.context || record?.appMode || record?.captureMode,
          languageFallback,
        );

  return {
    ...mapped,
    id: measurementRecordId(record),
    variant,
    captureMode: record?.captureMode || null,
    status: record?.status || null,
    upload: record?.upload || null,
    uploadFailed:
      mapped.uploadFailed ||
      record?.status === "error" ||
      record?.upload === "failed",
    rawMeasurement: record,
  };
}

export function useRegionalMeasurementResults({
  uid,
  variants = [],
  pageSize = 20,
  status = null,
  recentWithinMs = null,
  segmentVariants = true,
  perVariantLimit = null,
  languageFallback = "en-GB",
  refreshKey = 0,
  fetchMeasurements = fetchRegionalMeasurements,
} = {}) {
  const previousRequestContextRef = useRef("");
  const [state, setState] = useState({
    rows: [],
    loading: Boolean(uid),
    loadingMore: false,
    hasMore: false,
    error: null,
  });
  const variantKey = useMemo(
    () =>
      (Array.isArray(variants) ? variants : [])
        .map((variant) => String(variant || "").trim())
        .filter(Boolean)
        .join("|"),
    [variants],
  );

  useEffect(() => {
    const selectedVariants = variantKey.split("|").filter(Boolean);
    const requestContextKey = [
      uid || "",
      variantKey,
      status || "",
      recentWithinMs || "",
      languageFallback || "",
    ].join("|");

    if (!uid || selectedVariants.length === 0) {
      previousRequestContextRef.current = "";
      setState({
        rows: [],
        loading: false,
        loadingMore: false,
        hasMore: false,
        error: null,
      });
      return undefined;
    }

    if (typeof fetchMeasurements !== "function") {
      previousRequestContextRef.current = "";
      setState({
        rows: [],
        loading: false,
        loadingMore: false,
        hasMore: false,
        error: null,
      });
      return undefined;
    }

    let cancelled = false;
    const isSameListContext =
      previousRequestContextRef.current === requestContextKey;
    previousRequestContextRef.current = requestContextKey;

    setState((previousState) => ({
      rows: isSameListContext ? previousState.rows : [],
      loading: !isSameListContext || previousState.rows.length === 0,
      loadingMore: isSameListContext && previousState.rows.length > 0,
      hasMore: isSameListContext ? previousState.hasMore : false,
      error: null,
    }));

    const requestParams = {
      variants: selectedVariants,
      status,
      limit: pageSize,
      segmentVariants,
    };

    if (recentWithinMs !== null && recentWithinMs !== undefined) {
      requestParams.recentWithinMs = recentWithinMs;
    }

    if (perVariantLimit !== null && perVariantLimit !== undefined) {
      requestParams.perVariantLimit = perVariantLimit;
    }

    fetchMeasurements(requestParams)
      .then((response) => {
        if (cancelled) return;

        const rows = Array.isArray(response?.measurements)
          ? response.measurements
              .map((record) =>
                mapBackendMeasurementToRow(record, languageFallback),
              )
              .filter((row) => row.id)
          : [];

        setState({
          rows,
          loading: false,
          loadingMore: false,
          hasMore: Boolean(response?.hasMore),
          error: null,
        });
      })
      .catch((error) => {
        if (cancelled) return;
        setState((previousState) => {
          const canPreserveRows =
            isSameListContext && previousState.rows.length > 0;

          return {
            rows: canPreserveRows ? previousState.rows : [],
            loading: false,
            loadingMore: false,
            hasMore: canPreserveRows ? previousState.hasMore : false,
            error,
          };
        });
      });

    return () => {
      cancelled = true;
    };
  }, [
    fetchMeasurements,
    languageFallback,
    pageSize,
    perVariantLimit,
    recentWithinMs,
    refreshKey,
    segmentVariants,
    status,
    uid,
    variantKey,
  ]);

  return state;
}
